import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Setting categories
 */
export enum SettingCategory {
  PRINTER = 'printer',
  RECEIPT = 'receipt',
  TAX = 'tax',
  LAYOUT = 'layout',
  CASH_DRAWER = 'cash-drawer',
  SYSTEM = 'system',
  PAYMENT = 'payment'
}

/**
 * Setting value with metadata
 */
export interface SettingValue {
  key: string;
  value: any;
  defaultValue: any;
  category: SettingCategory;
  description: string;
  isDefault: boolean;
  canReset: boolean;
  lastModified?: {
    by: string;
    at: string;
  };
}

/**
 * Settings comparison diff
 */
export interface SettingsDiff {
  key: string;
  outlet1Value: any;
  outlet2Value: any;
  isDifferent: boolean;
  outlet1IsDefault: boolean;
  outlet2IsDefault: boolean;
}

/**
 * Settings State Interface
 */
export interface SettingsState {
  // Current outlet settings
  currentOutletId: string | null;
  outletSettings: Record<string, any>; // key -> value
  defaultSettings: Record<string, any>; // key -> default value

  // Comparison/multi-outlet
  comparisonOutletIds: string[];
  comparisonSettings: Record<string, Record<string, any>>; // outletId -> (key -> value)

  // UI state
  selectedCategory: SettingCategory | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  successMessage: string | null;

  // Unsaved changes
  unsavedChanges: Record<string, any>; // key -> value
  hasChanges: boolean;
}

/**
 * Initial state
 */
const initialState: SettingsState = {
  currentOutletId: null,
  outletSettings: {},
  defaultSettings: {},
  comparisonOutletIds: [],
  comparisonSettings: {},
  selectedCategory: null,
  isLoading: false,
  isSaving: false,
  error: null,
  successMessage: null,
  unsavedChanges: {},
  hasChanges: false,
};

/**
 * Settings Slice
 */
export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Setters
    setCurrentOutletId: (state, action: PayloadAction<string | null>) => {
      state.currentOutletId = action.payload;
      // Clear unsaved changes when switching outlets
      state.unsavedChanges = {};
      state.hasChanges = false;
    },

    setOutletSettings: (state, action: PayloadAction<Record<string, any>>) => {
      state.outletSettings = action.payload;
    },

    setDefaultSettings: (state, action: PayloadAction<Record<string, any>>) => {
      state.defaultSettings = action.payload;
    },

    setSelectedCategory: (state, action: PayloadAction<SettingCategory | null>) => {
      state.selectedCategory = action.payload;
    },

    // Comparison
    setComparisonOutletIds: (state, action: PayloadAction<string[]>) => {
      state.comparisonOutletIds = action.payload;
    },

    setComparisonSettings: (state, action: PayloadAction<Record<string, Record<string, any>>>) => {
      state.comparisonSettings = action.payload;
    },

    addOutletToComparison: (state, action: PayloadAction<string>) => {
      if (!state.comparisonOutletIds.includes(action.payload)) {
        state.comparisonOutletIds.push(action.payload);
      }
    },

    removeOutletFromComparison: (state, action: PayloadAction<string>) => {
      state.comparisonOutletIds = state.comparisonOutletIds.filter(id => id !== action.payload);
      delete state.comparisonSettings[action.payload];
    },

    // UI State
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
      // Auto-clear success message after setting
      if (action.payload) {
        setTimeout(() => {
          state.successMessage = null;
        }, 3000);
      }
    },

    // Changes tracking
    updateLocalSetting: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const { key, value } = action.payload;
      state.unsavedChanges[key] = value;
      state.hasChanges = Object.keys(state.unsavedChanges).length > 0;
    },

    clearUnsavedChanges: (state) => {
      state.unsavedChanges = {};
      state.hasChanges = false;
    },

    resetUnsavedChanges: (state) => {
      state.unsavedChanges = {};
      state.hasChanges = false;
    },

    // Reset all state
    resetSettingsState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    // Clear settings on logout
    builder.addMatcher(
      (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
      () => initialState
    );
  },
});

// Export actions
export const {
  setCurrentOutletId,
  setOutletSettings,
  setDefaultSettings,
  setSelectedCategory,
  setComparisonOutletIds,
  setComparisonSettings,
  addOutletToComparison,
  removeOutletFromComparison,
  setIsLoading,
  setIsSaving,
  setError,
  setSuccessMessage,
  updateLocalSetting,
  clearUnsavedChanges,
  resetUnsavedChanges,
  resetSettingsState,
} = settingsSlice.actions;

// Selectors
export const selectSettings = (state: RootState) => state.settings;
export const selectCurrentOutletId = (state: RootState) => state.settings.currentOutletId;
export const selectOutletSettings = (state: RootState) => state.settings.outletSettings;
export const selectDefaultSettings = (state: RootState) => state.settings.defaultSettings;
export const selectSelectedCategory = (state: RootState) => state.settings.selectedCategory;
export const selectUnsavedChanges = (state: RootState) => state.settings.unsavedChanges;
export const selectHasChanges = (state: RootState) => state.settings.hasChanges;
export const selectIsLoading = (state: RootState) => state.settings.isLoading;
export const selectIsSaving = (state: RootState) => state.settings.isSaving;
export const selectError = (state: RootState) => state.settings.error;
export const selectSuccessMessage = (state: RootState) => state.settings.successMessage;
export const selectComparisonOutletIds = (state: RootState) => state.settings.comparisonOutletIds;
export const selectComparisonSettings = (state: RootState) => state.settings.comparisonSettings;

// Selector for current settings by category
export const selectCurrentSettingsByCategory = (category: SettingCategory) => (state: RootState) => {
  const settings = state.settings.outletSettings;
  const filtered: Record<string, any> = {};

  Object.entries(settings).forEach(([key, value]) => {
    // Assuming settings have category prefix (e.g., 'printer.', 'receipt.')
    if (key.startsWith(category)) {
      filtered[key] = value;
    }
  });

  return filtered;
};

// Selector for checking if a setting has unsaved changes
export const selectHasUnsavedChange = (key: string) => (state: RootState) => {
  return key in state.settings.unsavedChanges;
};

// Selector for getting effective value (unsaved or saved)
export const selectEffectiveValue = (key: string) => (state: RootState) => {
  if (key in state.settings.unsavedChanges) {
    return state.settings.unsavedChanges[key];
  }
  return state.settings.outletSettings[key];
};

export default settingsSlice.reducer;
